<template>
  <v-dialog v-model="dialog" persistent max-width="400">
    <!-- Open button -->
    <template v-slot:activator="{ on: dialog }">
      <v-list-item v-on="dialog" @click.stop="dialog = true">
        <v-icon class="secondary--text">mdi-printer-pos-outline</v-icon>
        <v-list-item-title class="secondary--text">
          Imprimir ticket
        </v-list-item-title>
      </v-list-item>
    </template>
    <!-- Dialog -->
    <v-dialog v-model="dialog" max-width="500px">
      <v-card>
        <!-- Título -->
        <v-card-title>
          Imprimir ticket
        </v-card-title>
        <v-card-text>
          <!-- Selector de sistema operativo -->
          <v-select
            v-model="os"
            :items="osOptions"
            label="¿Desde qué dispositivo imprimes?"
            color="secondary"
            item-color="secondary"
            item-text="name"
            item-value="value"
            filled
            rounded
            dense
          ></v-select>
          <!-- Selector de impresora -->
          <v-select
            v-model="printer"
            :items="printers"
            label="Selecciona una impresora"
            color="secondary"
            item-color="secondary"
            item-text="name"
            item-value="mac"
            filled
            rounded
            dense
          ></v-select>
        </v-card-text>
        <!-- Acciones -->
        <v-card-actions>
          <v-spacer></v-spacer>
          <!-- Botón que cancela la operación -->
          <v-btn class="red--text" text @click="dialog = false">
            Cancelar
          </v-btn>
          <!-- Botón para imprimir   -->
          <v-btn
            :color="mainColor"
            text
            @click="print"
            :loading="isPrinting"
            :disabled="!printer || isPrinting"
          >
            Imprimir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
// Importar conector
import { ConectorPluginV3 } from "./PrinterConectorJavaScript";
import { ConectorEscposAndroid } from "./ConectorEscposAndroid";
import BusinessServices from "@/services/BusinessServices";
import LoggingService from "@/services/LoggingService";

// Importar getters
import { mapGetters } from "vuex";

export default {
  props: {
    saleData: {
      type: Object
    },
    products: {
      type: Array
    },
    customer: {
      type: Object
    }
  },
  data() {
    return {
      dialog: false,
      mainColor: "#1DDF96",
      saleId: "",
      printers: [],
      printer: null,
      os: this.$device.android ? "android" : "windows",
      osOptions: [
        { name: "Android", value: "android" },
        { name: "Windows", value: "windows" }
      ],
      isPrinting: false
    };
  },
  methods: {
    close() {
      this.dialog = false;
    },
    async print() {
      this.isPrinting = true;

      // Generar fecha
      const date = new Date();
      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      };
      const dateFormat = new Intl.DateTimeFormat("es-MX", options);
      const dateFormatted = dateFormat.format(date);

      // Obtener datos del negocio
      const { name: businessName } = await BusinessServices.getBusiness();

      // Obtener licensias
      const licenses = await BusinessServices.getPrinterLicense();

      if (this.os === "android") {
        await this.printAndroid({
          dateFormatted,
          businessName,
          license: licenses.android
        });
      } else {
        await this.printWindows({
          dateFormatted,
          businessName,
          license: licenses.desktop
        });
      }

      this.isPrinting = false;
    },
    async printWindows({ dateFormatted, businessName, license }) {
      let conector = new ConectorPluginV3(
        ConectorPluginV3.URL_PLUGIN_POR_DEFECTO,
        license
      );

      // Iniciar conector
      conector
        .Iniciar()
        .EstablecerAlineacion(ConectorPluginV3.ALINEACION_CENTRO)
        .Feed(1)
        .EscribirTexto(`${businessName}\n`)
        .EscribirTexto(`${dateFormatted}\n`)
        .Feed(1)
        .EstablecerAlineacion(ConectorPluginV3.ALINEACION_IZQUIERDA)
        .EscribirTexto("____________________\n")
        .Feed(1);

      // Recorrer productos
      for (let i = 0; i < this.products.length; i++) {
        const product = this.products[i];

        conector = conector
          .EstablecerAlineacion(ConectorPluginV3.ALINEACION_IZQUIERDA)
          .EscribirTexto(`${product.name} (x${product.quantity})\n`)
          .EstablecerAlineacion(ConectorPluginV3.ALINEACION_DERECHA)
          .EscribirTexto(`$${product.amount}\n`);
      }

      // Agregar total
      conector
        .Feed(1)
        .EscribirTexto("____________________\n")
        .EscribirTexto(`TOTAL: $${this.saleData.total}\n`)
        .EscribirTexto("____________________\n");

      // Agregar agradecimiento
      conector
        .EstablecerAlineacion(ConectorPluginV3.ALINEACION_CENTRO)
        .EstablecerEnfatizado(true)
        .EstablecerTamañoFuente(1, 1)
        .EscribirTexto("Gracias por su compra\n")
        .Feed(2)
        .Corte(1);

      try {
        const respuesta = await conector.imprimirEn(this.printer);

        if (respuesta === true) {
          alert("Impreso correctamente");
        } else {
          alert("Error: " + respuesta);
        }
      } catch (e) {
        alert("Error imprimiendo: " + e.message);
      }
    },
    async printAndroid({ dateFormatted, businessName, license }) {
      let conector = new ConectorEscposAndroid(license);

      // Iniciar conector
      conector
        .Iniciar()
        .EstablecerAlineacion(ConectorEscposAndroid.ALINEACION_CENTRO)
        .Feed(1)
        .EscribirTexto(`${businessName}\n`)
        .EscribirTexto(`${dateFormatted}\n`)
        .Feed(1);

      // Imprimir cliente
      if (this.customer?.full_name) {
        conector
          .EstablecerAlineacion(ConectorEscposAndroid.ALINEACION_CENTRO)
          .EscribirTexto(`Cliente: ${this.customer.full_name}\n`)
          .Feed(1);
      }

      // Agregar separador
      conector
        .EstablecerAlineacion(ConectorEscposAndroid.ALINEACION_IZQUIERDA)
        .EscribirTexto("____________________\n")
        .Feed(1);

      // Recorrer productos
      for (let i = 0; i < this.products.length; i++) {
        const product = this.products[i];

        conector = conector
          .EstablecerAlineacion(ConectorEscposAndroid.ALINEACION_IZQUIERDA)
          .EscribirTexto(`${product.name} (x${product.quantity})\n`)
          .EstablecerAlineacion(ConectorEscposAndroid.ALINEACION_DERECHA)
          .EscribirTexto(`$${product.amount}\n`);
      }

      // Agregar total
      conector
        .Feed(1)
        .EscribirTexto("____________________\n")
        .EscribirTexto(`TOTAL: $${this.saleData.total}\n`)
        .EscribirTexto("____________________\n");

      // Agregar agradecimiento
      conector
        .Feed(1)
        .EstablecerAlineacion(ConectorEscposAndroid.ALINEACION_CENTRO)
        .EstablecerEnfatizado(true)
        .EstablecerTamañoFuente(1, 1)
        .EscribirTexto("Gracias por su compra\n")
        .Feed(2)
        .Corte(1);

      try {
        const respuesta = await conector.imprimirEn(this.printer);

        if (respuesta === true) {
          alert("Impreso correctamente");
        } else {
          alert("Error: " + respuesta);
        }
      } catch (e) {
        alert("Error imprimiendo: " + e.message);
      }
    }
  },
  mounted: async function() {
    // Obtener impresoras
    const printers = await ConectorPluginV3.obtenerImpresoras();

    // Convertir impresoras a formato de v-select
    this.printers = printers
      .map(printer => {
        // if string return object with name, else return the printer obj
        if (typeof printer === "string") {
          return { name: printer, mac: printer };
        }

        return printer;
      })
      .filter(printer => {
        return printer.name && printer.name.length > 0;
      })
      .sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
  },
  computed: {
    ...mapGetters(["getProfile"])
  }
};
</script>
